import styled from 'styled-components';
import { mediaLgMax, mediaMdMax, mediaMdMin, mediaSmMax } from 'helpers/breakpoints';
import NextImageWithShimmer from 'components/image/NextImageWithShimmer';
import { useScrollSnap } from 'hooks/useScrollSnap';
import boatsetterPaths from 'utils/boatsetterPaths';

const locationList = [
  {
    name: 'Puerto Rico',
    imageS3Path: 'spa/homepage/explore/PR.png',
    link: boatsetterPaths.locations.puertoRico,
  },
  {
    name: 'Miami',
    imageS3Path: 'spa/homepage/explore/Miami.png',
    link: boatsetterPaths.locations.miami,
  },
  {
    name: 'U.S. Virgin Islands',
    imageS3Path: 'spa/homepage/explore/USVI.png',
    link: '/a/boat-rentals/search?sort_by=recommended&per_page=18&search_mode=&action=search&near=U.S.%20Virgin%20Islands&area=&page=1&ne_lat=18.4579577&ne_lng=-64.2900939&sw_lat=17.5974203&sw_lng=-65.3431107&zoom_level=11',
  },
  {
    name: 'Fort Lauderdale',
    imageS3Path: 'spa/homepage/explore/FortLauderdale.png',
    link: boatsetterPaths.locations.fortLauderdale,
  },
  {
    name: 'Austin',
    imageS3Path: 'spa/homepage/explore/Austin-jan-2026.png',
    link: boatsetterPaths.locations.austin,
  },
  {
    name: 'West Palm Beach',
    imageS3Path: 'spa/homepage/explore/WestPalmBeach.png',
    link: boatsetterPaths.locations.westPalmBeach,
  },
  {
    name: 'Phoenix',
    imageS3Path: 'spa/homepage/explore/Phoenix-jan-2026.png',
    link: boatsetterPaths.locations.phoenix,
  },
  {
    name: 'Dallas',
    imageS3Path: 'spa/homepage/explore/Dallas.png',
    link: boatsetterPaths.locations.dallas,
  },
  {
    name: 'San Diego',
    imageS3Path: 'spa/homepage/explore/SanDiego-jan-2026.png',
    link: boatsetterPaths.locations.sanDiego,
  },
  {
    name: 'Chicago',
    imageS3Path: 'spa/homepage/explore/Chicago-040225.png',
    link: boatsetterPaths.locations.chicago,
  },
  {
    name: 'Tampa',
    imageS3Path: 'spa/homepage/explore/Tampa-250924.jpg',
    link: boatsetterPaths.locations.tampaBay,
  },
  {
    name: 'Los Angeles',
    imageS3Path: 'spa/homepage/explore/LosAngeles.png',
    link: boatsetterPaths.locations.losAngeles,
  },
  {
    name: 'Fort Myers',
    imageS3Path: 'spa/homepage/explore/FortMyers.png',
    link: boatsetterPaths.locations.fortMyers,
  },
  {
    name: 'New York',
    imageS3Path: 'spa/homepage/explore/NewYork.png',
    link: boatsetterPaths.locations.newYorkCity,
  },
  {
    name: 'Seattle',
    imageS3Path: 'spa/homepage/explore/Seattle.png',
    link: boatsetterPaths.locations.seattle,
  },
];

export const Explore = () => {
  const { scrollRef, canScrollPrev, canScrollNext, scrollToPrev, scrollToNext } = useScrollSnap();

  return (
    <ExploreWrapper data-testid="homepage-review-wrapper" className="explore">
      <div className="wrapper">
        <h2>Explore destinations by boat</h2>
        <p>Book a private boat rental, just about anywhere.</p>
        <div className="nav">
          <button
            type="button"
            onClick={scrollToPrev}
            className="arrow-btn"
            disabled={!canScrollPrev}
            aria-label="Previous destinations"
            data-testid="homepage-review-left-arrow"
          >
            <img src="/images/homepage/carousel-left.svg" alt="" width={10} height={17} />
          </button>
          <button
            type="button"
            onClick={scrollToNext}
            className="arrow-btn next"
            disabled={!canScrollNext}
            aria-label="Next destinations"
            data-testid="homepage-review-right-arrow"
          >
            <img src="/images/homepage/carousel-left.svg" alt="" width={10} height={17} />
          </button>
        </div>

        <ScrollContainer ref={scrollRef}>
          {locationList.map((location, index) => (
            <Slide key={location.name} data-slide-index={index}>
              <a className="location-card" href={location.link}>
                <div className="image-container">
                  <NextImageWithShimmer
                    src={location.imageS3Path}
                    alt={location.name}
                    fill
                    loading={index < 4 ? 'eager' : 'lazy'}
                    sizes="(min-width: 960px) 246px, (min-width: 640px) 240px, 200px"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <span>{location.name}</span>
              </a>
            </Slide>
          ))}
        </ScrollContainer>
      </div>
    </ExploreWrapper>
  );
};

const ExploreWrapper = styled.div`
  margin: 90px auto 90px;
  max-width: 1076px;
  padding: 0 20px;

  @media ${mediaMdMin} {
    margin: 175px auto 116px;
  }

  .wrapper {
    display: grid;
    grid-template-areas:
      'heading nav'
      'subtitle nav'
      'boats boats';
    grid-template-columns: 1fr auto;

    & > p {
      grid-area: subtitle;
      max-width: calc(100% - 104px);
      line-height: 34px;
      font-size: 20px;
      font-weight: 400;

      @media ${mediaSmMax} {
        font-size: 15px;
        line-height: 22px;
        max-width: 100%;
      }
    }
  }

  h2 {
    font-weight: 700;
    font-size: 32px;
    line-height: 34px;
    grid-area: heading;
    max-width: calc(100% - 104px);
    min-width: 0;

    @media ${mediaLgMax} {
      width: 100%;
      max-width: 100%;
      font-size: 24px;
      line-height: 24px;
    }
  }

  .nav {
    grid-area: nav;
    display: flex;
    align-self: center;
    gap: 20px;
    z-index: 200;

    @media ${mediaMdMax} {
      display: none;
    }
  }

  .arrow-btn {
    cursor: pointer;
    background: #f5f7f9;
    padding: 0;
    border-radius: 50%;
    height: 47px;
    width: 47px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;

    &.next {
      transform: rotateZ(180deg);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    img {
      height: 12px;
    }
  }
`;

const ScrollContainer = styled.div`
  grid-area: boats;
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 30px;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${mediaMdMax} {
    gap: 20px;
  }
`;

const Slide = styled.div`
  flex-shrink: 0;
  scroll-snap-align: start;

  /* Desktop (>=960px): 4 slides visible, 3 gaps of 30px */
  width: calc((100% - 90px) / 4);

  /* Tablet (640-959px): ~2.8 slides visible, 2 full gaps of 20px */
  @media ${mediaMdMax} {
    width: calc((100% - 40px) / 2.8);
  }

  /* Mobile (<640px): ~1.8 slides visible, 1 full gap of 20px */
  @media ${mediaSmMax} {
    width: calc((100% - 20px) / 1.8);
  }

  .location-card {
    display: flex;
    flex-direction: column;
    text-decoration: none;
  }

  .image-container {
    position: relative;
    aspect-ratio: 205 / 156;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;
  }

  span {
    font-size: 18px;
    font-weight: 600;
    line-height: 120%;
    color: #17233c;
  }
`;
